import React from "react";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkBreaks from "remark-breaks";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";

const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    code: [...(defaultSchema.attributes?.code || []), ["className"]],
    a: [...(defaultSchema.attributes?.a || []), ["target"], ["rel"]],
  },
};

type Props = {
  content: string;
  compact?: boolean;
  variant?: "user" | "agent";
};

const MarkdownRenderer: React.FC<Props> = ({ content, compact, variant = "agent" }) => {
  const components: Components = {
    code({ inline, className, children }: any) {
      const match = /language-(\w+)/.exec(className || "");
      if (!inline) {
        return (
          <div className="my-2">
            <SyntaxHighlighter
              language={match?.[1] || "text"}
              PreTag="div"
              style={vscDarkPlus}
              customStyle={{
                borderRadius: '0.5rem',
                padding: '1rem',
                fontSize: compact ? '0.75rem' : '0.875rem',
              }}
            >
              {String(children).replace(/\n$/, "")}
            </SyntaxHighlighter>
          </div>
        );
      }
      return (
        <code
          className={`px-1.5 py-0.5 rounded font-mono text-sm ${
            variant === "user"
              ? "bg-blue-700 bg-opacity-50"
              : "bg-gray-300 text-gray-900"
          }`}
        >
          {children}
        </code>
      );
    },
    a({ href, children }) {
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={`font-medium underline ${
            variant === "user"
              ? "text-blue-100 hover:text-white"
              : "text-blue-600 hover:text-blue-800"
          }`}
        >
          {children}
        </a>
      );
    },
    table({ children }) {
      return (
        <div className="overflow-x-auto my-3">
          <table className="min-w-full border-collapse border border-gray-300">
            {children}
          </table>
        </div>
      );
    },
    thead({ children }) {
      return (
        <thead className={variant === "user" ? "bg-blue-700" : "bg-gray-100"}>
          {children}
        </thead>
      );
    },
    th({ children }) {
      return (
        <th className="border border-gray-300 px-3 py-2 text-left font-semibold">
          {children}
        </th>
      );
    },
    td({ children }) {
      return (
        <td className="border border-gray-300 px-3 py-2">
          {children}
        </td>
      );
    },
    ul({ children }) {
      return <ul className="list-disc pl-6 my-3 space-y-2">{children}</ul>;
    },
    ol({ children }) {
      return <ol className="list-decimal pl-6 my-3 space-y-2">{children}</ol>;
    },
    li({ children }) {
      return <li className="leading-relaxed pl-1">{children}</li>;
    },
    p({ children }) {
      return <p className="my-2.5 leading-relaxed">{children}</p>;
    },
    h1({ children }) {
      return (
        <h1 className="text-2xl font-bold my-4 pb-2 border-b border-current/20">
          {children}
        </h1>
      );
    },
    h2({ children }) {
      return (
        <h2 className="text-xl font-bold my-3 pb-1.5 border-b border-current/10">
          {children}
        </h2>
      );
    },
    h3({ children }) {
      return (
        <h3 className="text-lg font-semibold my-2.5">
          {children}
        </h3>
      );
    },
    strong({ children }) {
      return <strong className="font-bold text-current">{children}</strong>;
    },
    em({ children }) {
      return <em className="italic opacity-90">{children}</em>;
    },
    blockquote({ children }) {
      return (
        <blockquote
          className={`border-l-4 pl-4 my-3 py-2 italic rounded-r ${
            variant === "user"
              ? "border-blue-300 bg-blue-700/10"
              : "border-gray-400 bg-gray-100"
          }`}
        >
          {children}
        </blockquote>
      );
    },
    hr() {
      return (
        <hr
          className={`my-4 border-t-2 ${
            variant === "user"
              ? "border-blue-400/30"
              : "border-gray-300"
          }`}
        />
      );
    },
  };

  return (
    <div className={compact ? "text-sm" : "text-base"}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        rehypePlugins={[[rehypeSanitize, schema]]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default MarkdownRenderer;
